/**
 * Lookups for a single J-code's `documents` array within auth-on-file entries.
 *
 * @module getAllDocsForSingleJCode
 */
import { AuthOnFile } from "../data-model/medicalPaOrdersModel";
import { DocumentTypesForOrder } from "../enums/documentTypes";

/**
 * Builds a synthetic auth-letter document row from nested auth-letter data.
 *
 * @param authLetterPath - Path stored on `j_code.auth_letter.auth_letter_path`
 * @returns Single document-like row or an empty array when no path exists
 */
function buildAuthLetterDocumentFallback(authLetterPath?: string | null) {
  if (!authLetterPath?.trim()) {
    return [];
  }
  return [
    {
      doc_type: DocumentTypesForOrder.AUTH_LETTER,
      doc_path: authLetterPath,
    },
  ];
}

/**
 * Retrieves all documents associated with a specific J-code from a medical PA order.
 *
 * Searches through the order's `auth_on_file.entries` (falling back to
 * `auth_entries`) to find the auth entry containing the matching J-code,
 * then returns that J-code's `documents` array.
 *
 * @param jCodeId - The unique identifier of the J-code to look up.
 * @param authOnFileDetails - Auth-on-file payload whose auth entries will be searched.
 * @returns The {@link JCodeDocuments} array for the matching J-code, or `undefined`
 *          if the J-code or its documents are not found.
 */
export const getAllDocsForSingleJCode = (
  jCodeId: string,
  authOnFileDetails: AuthOnFile,
) => {
  const authEntries =
    authOnFileDetails?.entries ?? authOnFileDetails?.auth_entries;
  const jCode = authEntries?.find((authEntry) =>
    authEntry?.j_codes?.find((jCode) => jCode?.id === jCodeId),
  );
  const jCodeRow = jCode?.j_codes?.find(
    (innerJCode) => innerJCode?.id === jCodeId,
  );
  return (
    jCodeRow?.documents ??
    buildAuthLetterDocumentFallback(jCodeRow?.auth_letter?.auth_letter_path)
  );
};

/**
 * Retrieves all documents associated with a specific J-code from a medical PA order.
 *
 * Searches through the order's `auth_on_file.entries` (falling back to `auth_entries`)
 * to find the auth entry containing the matching J-code, then returns that J-code's
 * `documents` array.
 *
 * @param jCodeId - The unique identifier of the J-code to look up.
 * @param authOnFileDetails - Auth-on-file payload whose auth entries will be searched.
 * @returns The {@link JCodeDocuments} array for the matching J-code, or `undefined`
 *          if the J-code or its documents are not found.
 */
export const getAllDocsForSingleJCodeFromGeneralOrderDetails = (
  jCodeId: string,
  authOnFileDetails: AuthOnFile,
) => {
  const authEntries =
    authOnFileDetails?.entries ?? authOnFileDetails?.auth_entries;
  const jCode = authEntries?.find((authEntry) =>
    authEntry?.j_codes?.find((jCode) => jCode?.id === jCodeId),
  );
  const jCodeRow = jCode?.j_codes?.find(
    (innerJCode) => innerJCode?.id === jCodeId,
  );
  return (
    jCodeRow?.documents ??
    buildAuthLetterDocumentFallback(jCodeRow?.auth_letter?.auth_letter_path)
  );
};
